import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { setToken } from "../utils/api";

const API = process.env.REACT_APP_BACKEND_URL;

export default function RegisterPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [form, setForm] = useState({ name: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [done, setDone] = useState(false);

  const valid =
    form.name.trim().length >= 2 &&
    form.password.length >= 6 &&
    form.password === form.confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, name: form.name.trim(), password: form.password }),
      });
      const d = await res.json();
      if (!res.ok) { toast.error(d.detail || "Error al registrarse"); setLoading(false); return; }
      setToken(d.token);
      setDone(true);
      setTimeout(() => navigate("/home"), 1800);
    } catch {
      toast.error("Error de conexión");
    }
    setLoading(false);
  };

  if (!token) return (
    <div className="min-h-screen bg-[#111] flex items-center justify-center p-6">
      <p className="text-gray-400">Link de invitación inválido o expirado.</p>
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-[#111] flex items-center justify-center p-6">
      <div className="text-center">
        <CheckCircle2 size={40} className="text-green-400 mx-auto mb-3" />
        <p className="text-white font-bold text-lg">¡Cuenta creada!</p>
        <p className="text-gray-400 text-sm mt-1">Redirigiendo al panel...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#111] flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="w-10 h-10 bg-[#FF4F00] rounded-xl flex items-center justify-center">
            <span className="text-white font-black text-lg">S</span>
          </div>
        </div>

        <h1 className="text-2xl font-black text-white text-center mb-1">Crear tu cuenta</h1>
        <p className="text-gray-500 text-sm text-center mb-8">Fuiste invitado/a a SitiosYA</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide block mb-1.5">
              Tu nombre
            </label>
            <input
              data-testid="register-name"
              type="text"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="María García"
              className="w-full bg-white/[0.06] border border-white/[0.12] rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#FF4F00]/60"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide block mb-1.5">
              Contraseña
            </label>
            <div className="relative">
              <input
                data-testid="register-password"
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Mínimo 6 caracteres"
                className="w-full bg-white/[0.06] border border-white/[0.12] rounded-xl px-4 py-3 pr-11 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#FF4F00]/60"
              />
              <button type="button" onClick={() => setShowPw(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide block mb-1.5">
              Confirmar contraseña
            </label>
            <input
              data-testid="register-confirm"
              type="password"
              value={form.confirm}
              onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
              placeholder="Repetir contraseña"
              className={`w-full bg-white/[0.06] border rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none ${
                form.confirm && form.confirm !== form.password
                  ? "border-red-500/60"
                  : "border-white/[0.12] focus:border-[#FF4F00]/60"
              }`}
            />
            {form.confirm && form.confirm !== form.password && (
              <p className="text-red-400 text-xs mt-1">Las contraseñas no coinciden</p>
            )}
          </div>

          <button
            data-testid="register-submit"
            type="submit"
            disabled={!valid || loading}
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#FF4F00] text-white font-bold rounded-xl disabled:opacity-50 hover:bg-orange-600 transition-colors mt-2"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            {loading ? "Creando cuenta..." : "Crear mi cuenta"}
          </button>
        </form>
      </div>
    </div>
  );
}
